<template>
	<div class="full-image" v-if="show" tabindex="0" ref="viewer" @keydown="onKeydown"
		@mouseup="onDragEnd" @mouseleave="onDragEnd">
		<div class="mask" @click="close"></div>
		<div class="image-box">
			<div class="nav-zone nav-zone-left" v-show="!isZoomed" @click.stop="onPrev"></div>
			<div class="nav-zone nav-zone-right" v-show="!isZoomed" @click.stop="onNext"></div>
			<div class="image-stage" ref="stage" @wheel.prevent="onWheel" @dblclick.prevent="onDblClick">
				<img ref="previewImg" :key="url" :src="url" :style="imageTransformStyle"
					:class="{ 'is-dragging': dragging }"
					@mousedown.prevent="onDragStart" @click.stop />
			</div>
			<div v-if="canPrev && !isZoomed" class="nav-btn nav-btn-left" @click.stop="onPrev">
				<i class="el-icon-arrow-left"></i>
			</div>
			<div v-if="canNext && !isZoomed" class="nav-btn nav-btn-right" @click.stop="onNext">
				<i class="el-icon-arrow-right"></i>
			</div>
		</div>
		<div v-if="urls.length > 1" class="image-counter">{{ currentIndex + 1 }} / {{ urls.length }}</div>
		<div class="close" @click="close"><i class="el-icon-close"></i></div>
	</div>
</template>

<script>
export default {
	name: "fullImage",
	data() {
		return {
			show: false,
			urls: [],
			currentIndex: 0,
			scale: 1,
			translateX: 0,
			translateY: 0,
			minScale: 0.5,
			maxScale: 5,
			dragging: false,
			dragStartX: 0,
			dragStartY: 0,
			dragOriginX: 0,
			dragOriginY: 0
		}
	},
	computed: {
		url() {
			return this.urls[this.currentIndex] || '';
		},
		canPrev() {
			return this.currentIndex > 0;
		},
		canNext() {
			return this.currentIndex < this.urls.length - 1;
		},
		isZoomed() {
			return this.scale > 1.05 || Math.abs(this.translateX) > 2 || Math.abs(this.translateY) > 2;
		},
		imageTransformStyle() {
			return {
				transform: `translate(calc(-50% + ${this.translateX}px), calc(-50% + ${this.translateY}px)) scale(${this.scale})`,
				cursor: this.scale > 1 ? (this.dragging ? 'grabbing' : 'grab') : 'zoom-in'
			};
		}
	},
	watch: {
		url() {
			this.resetTransform();
		}
	},
	mounted() {
		document.addEventListener('mousemove', this.onDragMove);
	},
	beforeDestroy() {
		document.removeEventListener('mousemove', this.onDragMove);
	},
	methods: {
		open(payload) {
			if (payload && typeof payload === 'object' && Array.isArray(payload.urls)) {
				this.openGallery(payload.urls, payload.index || 0);
				return;
			}
			if (typeof payload === 'string' && payload) {
				this.openGallery([payload], 0);
			}
		},
		openGallery(urls, index = 0) {
			this.urls = (urls || []).filter(Boolean);
			if (!this.urls.length) {
				return;
			}
			this.resetTransform();
			this.currentIndex = Math.min(Math.max(0, index), this.urls.length - 1);
			this.show = true;
			this.$nextTick(() => {
				this.$refs.viewer && this.$refs.viewer.focus();
			});
		},
		close() {
			this.show = false;
			this.resetTransform();
		},
		resetTransform() {
			this.scale = 1;
			this.translateX = 0;
			this.translateY = 0;
			this.dragging = false;
		},
		clampScale(value) {
			return Math.min(this.maxScale, Math.max(this.minScale, value));
		},
		setScale(nextScale, anchorX, anchorY) {
			const oldScale = this.scale;
			const scale = this.clampScale(nextScale);
			if (scale === oldScale) {
				return;
			}
			const stage = this.$refs.stage;
			if (stage && anchorX != null && anchorY != null) {
				const rect = stage.getBoundingClientRect();
				const cx = anchorX - rect.left - rect.width / 2;
				const cy = anchorY - rect.top - rect.height / 2;
				const ratio = scale / oldScale;
				this.translateX = cx - (cx - this.translateX) * ratio;
				this.translateY = cy - (cy - this.translateY) * ratio;
			}
			this.scale = scale;
			if (scale <= 1) {
				this.translateX = 0;
				this.translateY = 0;
			}
		},
		zoomIn() {
			this.setScale(this.scale + 0.25);
		},
		zoomOut() {
			this.setScale(this.scale - 0.25);
		},
		onWheel(e) {
			const delta = e.deltaY > 0 ? -0.12 : 0.12;
			this.setScale(this.scale + delta, e.clientX, e.clientY);
		},
		onDblClick(e) {
			if (this.scale > 1.05) {
				this.resetTransform();
			} else {
				this.setScale(2.5, e.clientX, e.clientY);
			}
		},
		onDragStart(e) {
			if (this.scale <= 1 || e.button !== 0) {
				return;
			}
			this.dragging = true;
			this.dragStartX = e.clientX;
			this.dragStartY = e.clientY;
			this.dragOriginX = this.translateX;
			this.dragOriginY = this.translateY;
		},
		onDragMove(e) {
			if (!this.dragging) {
				return;
			}
			this.translateX = this.dragOriginX + (e.clientX - this.dragStartX);
			this.translateY = this.dragOriginY + (e.clientY - this.dragStartY);
		},
		onDragEnd() {
			this.dragging = false;
		},
		onPrev() {
			if (this.canPrev) {
				this.currentIndex--;
			}
		},
		onNext() {
			if (this.canNext) {
				this.currentIndex++;
			}
		},
		onKeydown(e) {
			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				if (this.isZoomed) {
					this.translateX += 40;
				} else {
					this.onPrev();
				}
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				if (this.isZoomed) {
					this.translateX -= 40;
				} else {
					this.onNext();
				}
			} else if (e.key === 'Escape') {
				e.preventDefault();
				if (this.isZoomed) {
					this.resetTransform();
				} else {
					this.close();
				}
			} else if (e.key === '+' || e.key === '=') {
				e.preventDefault();
				this.zoomIn();
			} else if (e.key === '-') {
				e.preventDefault();
				this.zoomOut();
			} else if (e.key === '0') {
				e.preventDefault();
				this.resetTransform();
			}
		}
	}
}
</script>

<style lang="scss">
.full-image {
	position: fixed;
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
	bottom: 0;
	right: 0;
	z-index: 9999;
	outline: none;

	.mask {
		position: fixed;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.85);
	}

	.image-box {
		position: relative;
		width: 100%;
		height: 100%;

		.image-stage {
			position: absolute;
			left: 0;
			top: 0;
			right: 0;
			bottom: 0;
			overflow: hidden;
			z-index: 2;
		}

		img {
			position: absolute;
			left: 50%;
			top: 50%;
			max-height: 92%;
			max-width: 92%;
			user-select: none;
			transform-origin: center center;
			transition: none;
			will-change: transform;

			&.is-dragging {
				transition: none;
			}
		}

		.nav-zone {
			position: absolute;
			top: 0;
			bottom: 0;
			width: 28%;
			z-index: 3;
			cursor: pointer;
		}

		.nav-zone-left {
			left: 0;
		}

		.nav-zone-right {
			right: 0;
		}

		.nav-btn {
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			width: 44px;
			height: 44px;
			border-radius: 50%;
			background: rgba(0, 0, 0, 0.45);
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			z-index: 4;
			transition: background 0.2s ease;

			i {
				font-size: 22px;
				color: #fff;
			}

			&:hover {
				background: rgba(0, 0, 0, 0.65);
			}
		}

		.nav-btn-left {
			left: 24px;
		}

		.nav-btn-right {
			right: 24px;
		}
	}

	.image-counter {
		position: fixed;
		left: 50%;
		bottom: 28px;
		transform: translateX(-50%);
		padding: 6px 14px;
		border-radius: 16px;
		background: rgba(0, 0, 0, 0.45);
		color: #fff;
		font-size: 13px;
		z-index: 5;
		user-select: none;
	}

	.close {
		display: flex;
		align-items: center;
		justify-content: center;
		position: fixed;
		top: 20px;
		right: 20px;
		cursor: pointer;
		background: rgba(0, 0, 0, 0.45);
		border-radius: 50%;
		padding: 10px;
		z-index: 5;
		transition: background 0.2s ease;

		&:hover {
			background: rgba(0, 0, 0, 0.65);
		}

		i {
			font-weight: bold;
			font-size: 20px;
			color: white;
		}
	}
}
</style>
